<template>
  <Map
    @ready="onMapReady"
    @location-found="onLocationFound"
    :zoom.sync="zoom"
    :center.sync="center"
  >
    <!-- Campus polygons -->
    <template v-for="campus in campuses">
      <MapPolygon
        :key="campus._id"
        v-if="campus.bounds && campus.bounds.length"
        :bounds="campus.bounds"
        :color="campus.color"
      />
    </template>

    <template v-for="location in locations">
      <!-- Location indicators -->
      <MapIndicator
        :key="`${location._id}-indicator`"
        :coordinates="[location.lat, location.lng]"
        :hidden="(zoom <= zoomThreshold.visible) || isMarkerVisible(location)"
        :bordered="zoom > zoomThreshold.bordered"
        :popup-content="getPopupContent(location)"
        :color="(alertIsLive(location)) ? 'red' : '#500b76'"
        @click="setActiveLocations([location])"
        @keypress="setActiveLocations([location])"
      />

      <!-- Location markers -->
      <MapMarker
        :key="`${location._id}-marker`"
        :coordinates="[location.lat, location.lng]"
        :icon="location.category.icon"
        :icon-options="{ color: (alertIsLive(location)) ? 'red' : null }"
        :popup-content="getPopupContent(location)"
        :hidden="!isMarkerVisible(location)"
        @click="setActiveLocations([location])"
      />

      <!-- Emergency phones -->
      <MapMarker
        v-for="(emergencyPhone, index) in location.emergencyPhones"
        :key="`${location._id}-phone-${index}`"
        :coordinates="emergencyPhone.coordinates"
        icon="fa-phone"
        :popup-content="getPopupContent(emergencyPhone)"
        :hidden="!filteredCategoriesStatic.emergencyPhones && !isActiveLocation(location)"
      />
    </template>

    <!-- Transit stops -->
    <template v-for="transitStop in transitStops">
      <MapMarker
        :key="`${transitStop._id}-marker`"
        :coordinates="[transitStop.lat, transitStop.lng]"
        icon="fa-bus"
        :popup-content="getPopupContent(transitStop)"
        :icon-options="{ color: 'yellow' }"
        :hidden="!filteredCategoriesStatic.transit"
        @click="setActiveTransitStop(transitStop)"
      />
    </template>

    <template v-if="filteredCategoriesStatic.transit">
      <MapMarker
        :coordinates="[44.941732, -93.191549]"
        icon="fa-bus"
        popup-content="St. Paul Shuttle Stop"
        @click="openShuttleTracker('stp')"
      />
      <MapMarker
        :coordinates="[44.973962, -93.277572]"
        icon="fa-bus"
        popup-content="Minneapolis Shuttle Stop"
        @click="openShuttleTracker('mpls')"
      />

      <template v-for="campusShuttle in activeCampusShuttles">
        <CampusShuttle
          :key="campusShuttle.id"
          :id="campusShuttle.id"
        />
      </template>
    </template>

    <!-- User location -->
    <MapMarker
      v-if="currentLocation"
      :coordinates="currentLocation"
      icon="fa-location-arrow"
      popup-content="Current Location"
    />

    <MapControl position="topleft">
      <div class="leaflet-bar">
        <a class="leaflet-control-button" title="Home" role="button" aria-label="Pan to the current campus" @click="onControlClick('campus')">
          <i class="fa fa-home"></i>
        </a>
      </div>
      <div class="leaflet-bar">
        <a class="leaflet-control-button" title="Directions" role="button" aria-label="Get directions" @click="onControlClick('directions')">
          <i class="fa fa-map-signs"></i>
        </a>
      </div>
    </MapControl>

    <MapControl position="bottomleft">
      <v-tooltip right v-model="copyTooltip">
        <template v-slot:activator="{ on }">
          <div class="leaflet-bar">
            <a class="leaflet-control-button" title="Create link" role="button" aria-label="Create link" @click="copy()" v-on="on">
              <i class="fa fa-link"></i>
            </a>
          </div>
        </template>
        <span>Copied to clipboard</span>
      </v-tooltip>
    </MapControl>

    <!-- Right Mobile Buttons Container -->
    <div v-if="$vuetify.breakpoint.smAndDown">
      <v-slide-x-reverse-transition mode="out-in">
        <MapControl position="topright">
          <div class="leaflet-bar">
            <a class="leaflet-control-button" title="Home" role="button" aria-label="Focus on your current location" @click="onControlClick('current-location')">
              <i class="fa fa-location-arrow"></i>
            </a>
          </div>
          <div class="leaflet-bar">
            <a class="leaflet-control-button" title="Home" role="button" aria-label="Show the search menu" @click="onControlClick('search')">
              <i class="fa fa-search"></i>
            </a>
          </div>
          <div class="leaflet-bar">
            <a class="leaflet-control-button" title="Home" role="button" aria-label="Show the filter buttons" @click="onControlClick('filter')">
              <i class="fa fa-filter"></i>
            </a>
          </div>
        </MapControl>
      </v-slide-x-reverse-transition>
    </div>
  </Map>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import L from 'leaflet'

import Map from '@/components/map/Map'
import MapMarker from '@/components/map/MapMarker'
import MapIndicator from '@/components/map/MapIndicator'
import MapPolygon from '@/components/map/MapPolygon'
import MapControl from '@/components/map/MapControl'

import CampusShuttle from '@/components/home/CampusShuttle'

export default {
  name: 'MapContainer',
  components: {
    Map,
    MapMarker,
    MapIndicator,
    MapPolygon,
    MapControl,
    CampusShuttle
  },
  data () {
    return {
      map: null,
      zoom: (this.$vuetify.breakpoint.smAndDown) ? 16 : 17,
      center: [44.941671, -93.192408],
      markers: {
        locations: L.layerGroup(),
        transitStops: L.featureGroup(),
        emergencyPhones: L.featureGroup()
      },
      liveTransit: L.featureGroup(),
      indicatorStatus: {
        visible: null,
        bordered: null
      },
      zoomThreshold: {
        visible: 16,
        bordered: 17
      },
      copyTooltip: false,
      copyTooltipTimeout: null
    }
  },
  watch: {
    activeLocations (to, from) {
      if (to === from || this.selectedLocation) return

      if (this.activeLocations.length >= 1) {
        this.focusLocations(this.activeLocations)
      } else {
        this.focusCampus(this.activeCampus)
      }
    },
    activeCampus (to, from) {
      if (to === from) return

      if (!this.activeLocations.length) {
        this.focusCampus(this.activeCampus)
      }
    },
    activeTransitStop () {
      if (!this.activeTransitStop) {
        this.focusCampus(this.activeCampus)
      }
    },
    selectedLocation (to, from) {
      if (to === from) return

      if (this.selectedLocation && Object.keys(this.selectedLocation).length) {
        this.focusLocations([this.selectedLocation])
      } else {
        this.focusCampus(this.activeCampus)
      }
    }
  },
  computed: {
    ...mapGetters([
      'campuses',
      'transitStops',
      'categories',
      'activeLocations',
      'activeCampus',
      'currentLocation',
      'selectedLocation',
      'routingControl',
      'activeTransitStop',
      'locations',
      'filteredCategories',
      'filteredTags',
      'activeCampusShuttles'
    ]),
    pageLink () {
      return `https://${window.location.hostname}${this.$route.fullPath}`
    },
    filteredCategoriesStatic () {
      return {
        transit: this.filteredCategories.includes('transit'),
        emergencyPhones: this.filteredCategories.includes('emergencyPhones')
      }
    },
    // This function is a fucking nightmare...
    filteredLocations () {
      let output = []

      this.filteredCategories.forEach(category => {
        // Get any active subcategories
        let activeSubcategories = []
        if (category.subcategories) {
          activeSubcategories = category.subcategories
            .filter(subcategory => subcategory.active)
            .map(subcategory => subcategory.name)
        }

        const locationOutput = this.locations.filter(location => {
          // If there are no active subcategories, filter based on the category...
          if (!activeSubcategories.length && location.category) {
            return location.category.name === category.name

          // ...otherwise, filter based on the subcategories
          } else if (location.subcategories) {
            return location.subcategories.some(subcategory => activeSubcategories.includes(subcategory))
          }
        })

        output = output.concat(locationOutput)
      })

      if (!this.filteredCategories.length && this.filteredTags.length) output = this.locations

      // Then filter the locations by tag
      output = output.filter(location => {
        if (!this.filteredTags.length) return true

        return location.tags && location.tags.some(tag => this.filteredTags.includes(tag))
      })

      return output
    }
  },
  async mounted () {
    // Vue events
    this.$root.$on('set-location-message', (data) => { this.setMessage(data._id, data.message) })
    this.$root.$on('set-live-transit', (layer) => { this.setLiveTransit(layer) })
    this.$root.$on('remove-live-transit', () => { this.removeLiveTransit() })
    this.$root.$on('create-marker', (data) => { this.createMarker(data) })
    this.$root.$on('focus-coordinates', (coordinates) => {
      this.center = coordinates
      this.zoom = 18
    })
    this.$root.$on('locate-user', () => { this.locateUser() })

    this.$emit('ready', this.map)
  },
  methods: {
    ...mapActions([
      'setActiveLocations'
    ]),
    ...mapMutations([
      'setActiveTransitStop',
      'setCurrentLocation'
    ]),
    onMapReady (event) {
      this.map = event
      this.routingControl.addTo(this.map)
    },
    getPopupContent (input) {
      return (typeof input.code !== 'undefined' && input.code !== '') ? `${input.name} (${input.code})` : input.name
    },
    isMarkerVisible (location) {
      const activeLocationsIds = this.activeLocations.map(location => location._id)
      const filteredLocationsIds = this.filteredLocations.map(location => location._id)

      // Merge and remove duplicates
      const ids = [...new Set([].concat(activeLocationsIds, filteredLocationsIds))]

      return ids.includes(location._id)
    },
    focusLocations (locations) {
      if (locations.length > 1) {
        const markers = locations.map((location) => L.marker([location.lat, location.lng]))
        const group = L.featureGroup(markers)

        this.center = group.getBounds().getCenter()
        this.zoom = 17
      } else if (locations.length === 1) {
        this.center = [locations[0].lat, locations[0].lng]
        this.zoom = 18
      }
    },
    async focusCampus (campus) {
      if (!campus) return

      const localCampus = this.campuses.filter((localCampus) => {
        return localCampus._id === campus._id
      })[0]

      if (localCampus && localCampus.bounds && localCampus.bounds.length) {
        const bounds = L.latLngBounds(localCampus.bounds)
        this.center = bounds.getCenter()
        this.zoom = 17
      }
    },
    setMessage (location, message) {
      const id = location._id

      const localLocation = this.locations.filter((location) => {
        return location._id === id
      })[0]

      localLocation.marker.bindTooltip(message, {
        offset: [13, -25],
        direction: 'right',
        permanent: true
      }).openTooltip()
    },
    onLocationFound (e) {
      this.setCurrentLocation(e.latlng)

      this.$root.$emit('user-located', e.latlng)
    },
    setLiveTransit (layer) {
      if (this.map.hasLayer(this.liveTransit)) {
        this.removeLiveTransit()
      }

      this.liveTransit = layer

      this.liveTransit.addTo(this.map)
      this.map.fitBounds(this.liveTransit.getBounds())
    },
    removeLiveTransit () {
      this.liveTransit.remove()
    },
    createMarker (data) {
      const popup = data.name

      // Set the location marker
      const marker = L.marker([data.lat, data.lng], {
        icon: L.ExtraMarkers.icon({
          icon: 'fa-location-arrow',
          markerColor: 'black',
          prefix: 'fa',
          extraClasses: 'icon-closed-initially'
        })
      })
        .bindPopup(popup)
        .on('mouseover', (e) => { e.target.openPopup() })
        .on('mouseout', (e) => { e.target.closePopup() })

      // Add marker to layer group
      this.markers.locations.addLayer(marker)
    },
    alertIsLive (location) {
      const now = new Date()

      if (location.alert) {
        const start = new Date(location.alert.start)
        const end = new Date(location.alert.end)

        return (start <= now && end > now)
      }

      return false
    },
    addControl (control) {
      this.map.addControl(control)
    },
    removeControl (control) {
      this.map.removeControl(control)
    },
    copy () {
      try {
        const el = document.createElement('textarea')
        el.value = this.pageLink
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)

        if (this.copyTooltip) {
          clearTimeout(this.copyTooltipTimeout)
        }

        this.copyTooltip = true
        this.copyTooltipTimeout = setTimeout(() => {
          this.copyTooltip = false
        }, 2000)
      } catch (e) {
        console.error(e)
      }
    },
    onControlClick (event) {
      switch (event) {
        case 'current-location':
          this.locateUser()
          break
        case 'campus':
          this.focusCampus(this.activeCampus)
          break
      }

      this.$emit('control-click', event)
    },
    locateUser () {
      this.map.locate({ setView: true, maxZoom: 17 })
    },
    isActiveLocation (location) {
      const foundLocations = this.activeLocations.filter(currLoc => currLoc._id === location._id)

      return !!foundLocations.length
    },
    openShuttleTracker (campus) {
      window.open(`https://bustracker.stthomas.edu/?campus=${campus}`, '_blank')
    }
  }
}
</script>

<style lang="scss">
#map-container {
  width: 100%;
  height: 100%;
  z-index: 0;

  position: absolute;
  top: 0;
  left: 0;

  transition: height 0.3s;

  .leaflet-routing-container {
    opacity: 0;
    height: 0;
    width: 0;
  }
}
</style>
